// Modules
import { useState } from "react";
import { motion } from "framer-motion";
import { FormattedMessage } from "react-intl";
import { differenceInDays } from "date-fns";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Box,
    Stack,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Tooltip
} from "@mui/material";
// Actions
import { PatientNotificationsSWR } from "@/actions/patient/notificationActions";
// Components
import { TrustpilotPopup } from "../../../Users/TrustpilotPopup";
// Helpers
import { asyncTryCatch } from "@/helpers/utils";
import api from "@/helpers/api";
import { API_ROUTES } from "@/helpers/routes";
import { useRefreshSession } from "@/helpers/userData";
import { ONBOARDING_PHASE_DAYS } from "@/helpers/constants";
// Assets
import { InfoOutlined } from "@mui/icons-material";

interface FeedbackModalProps {
    therapistName: string;
    therapistImage: string;
    isOpen: boolean;
    therapyStartDate: string | undefined;
    closeModal: (modalName: string) => void;
}

const FeedbackDialog = ({
    therapistName,
    therapistImage,
    isOpen,
    therapyStartDate,
    closeModal
}: FeedbackModalProps) => {
    const [rating, setRating] = useState<number | null>(null);
    const [feedback, setFeedback] = useState("");
    const [allowSharing, setAllowSharing] = useState(false);
    const [showTrustpilot, setShowTrustpilot] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { refreshNotifications } = PatientNotificationsSWR();
    const refreshSession = useRefreshSession();

    const handleOpenChange = () => {
        closeModal("feedback");
    };

    const handleRatingSelect = (value: number) => {
        setRating(value);
    };

    const handleRemindLater = async () => {
        api.post({
            path: API_ROUTES.patient.feedback,
            data: {
                postponed: true
            }
        }).then(() => {
            refreshSession();
        });
        handleOpenChange();
    };

    const handleSubmit = async () => {
        if (feedback.length < 10) {
            return;
        }

        setIsSubmitting(true);

        const { error } = await asyncTryCatch(async () => {
            return await api.post({
                path: API_ROUTES.patient.feedback,
                data: {
                    rating,
                    feedback,
                    type:
                        therapyStartDate &&
                        differenceInDays(new Date(), new Date(therapyStartDate)) <= ONBOARDING_PHASE_DAYS
                            ? "onboarding"
                            : "therapy_progress",
                    allow_sharing: allowSharing
                }
            });
        });

        setIsSubmitting(false);

        if (error) {
            return console.error("Error submitting feedback:", error);
        }

        if (rating !== null && rating > 7) {
            setShowTrustpilot(true);
        }
        // Refresh session and notificaitons swr to keep notificaitons up to date
        refreshSession();
        refreshNotifications();
        handleOpenChange();
    };

    const handleAllowSharingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAllowSharing(e.target.checked);
    };

    const handleClose = () => {
        handleOpenChange();
    };

    const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFeedback(e.target.value);
    };

    const onTrustpilotModalClose = (open: boolean) => {
        setShowTrustpilot(open);
        if (!open) {
            handleOpenChange();
        }
    };

    return (
        <>
            <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle textAlign="center">
                    <FormattedMessage id="recurring-feedback-title" />
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ py: 2 }}>
                        <Stack direction="row" spacing={0.5} justifyContent="space-between" sx={{ mb: 3 }}>
                            {Array.from({ length: 11 }, (_, i) => (
                                <Button
                                    key={i}
                                    variant={rating === i ? "contained" : "outlined"}
                                    sx={{
                                        minWidth: "40px",
                                        height: "40px",
                                        borderRadius: "50%",
                                        p: 0
                                    }}
                                    onClick={() => handleRatingSelect(i)}
                                >
                                    {i}
                                </Button>
                            ))}
                        </Stack>

                        {rating !== null && (
                            <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: "auto" }}
                                transition={{ duration: 0.4, ease: "easeOut" }}
                            >
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    <FormattedMessage
                                        id={rating <= 7 ? "recurring-feedback-negative" : "recurring-feedback-positive"}
                                    />
                                </Typography>

                                <TextField
                                    value={feedback}
                                    onChange={handleFeedbackChange}
                                    multiline
                                    rows={4}
                                    fullWidth
                                />

                                <FormControlLabel
                                    sx={{ mt: 1 }}
                                    control={<Checkbox checked={allowSharing} onChange={handleAllowSharingChange} />}
                                    label={
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography variant="body2">
                                                <FormattedMessage id="recurring-feedback-allow-sharing" />
                                            </Typography>
                                            <Tooltip
                                                title={
                                                    <FormattedMessage id="recurring-feedback-allow-sharing-tooltip" />
                                                }
                                            >
                                                <InfoOutlined fontSize="small" />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            </motion.div>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, justifyContent: "space-between" }}>
                    <Button variant="outlined" onClick={handleRemindLater}>
                        <FormattedMessage id="recurring-feedback-remind-later" />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={rating === null || feedback.length < 10 || isSubmitting}
                        endIcon={isSubmitting && <CircularProgress size={20} />}
                    >
                        <FormattedMessage id="submit" />
                    </Button>
                </DialogActions>
            </Dialog>

            <TrustpilotPopup
                isOpen={showTrustpilot}
                onClose={onTrustpilotModalClose}
                therapistName={therapistName}
                therapistImage={therapistImage}
            />
        </>
    );
};

export default FeedbackDialog;
